import fontkit from '@pdf-lib/fontkit';
import {
  PDFDocument,
  StandardFonts,
  TextRenderingMode,
  appendBezierCurve,
  beginText,
  clip,
  closePath,
  degrees,
  endText,
  endPath,
  moveTo,
  popGraphicsState,
  pushGraphicsState,
  rgb,
  rotateAndSkewTextDegreesAndTranslate,
  setFillingColor,
  setFontAndSize,
  setGraphicsState,
  setLineWidth,
  setStrokingColor,
  setTextRenderingMode,
  showText,
  type PDFEmbeddedPage,
  type PDFFont,
  type PDFImage,
  type PDFPage,
  type RGB,
} from 'pdf-lib';

import { PdfEngineError } from './errors.ts';
import { buildPdfFileName } from './filename.ts';
import { assertPdfScene } from './scene.ts';
import { calculateV46StickerCutLineMetrics } from './sticker-cut-line.ts';
import type {
  PdfArtifact,
  PdfExportDiagnostic,
  PdfExportOptions,
  PdfSceneV1,
  PdfStandardFontName,
  ResolvedPdfAsset,
  SceneAssetNode,
  SceneTextNode,
  SceneTransformMm,
} from './types.ts';

export const POINTS_PER_MM = 72 / 25.4;
const DEFAULT_RASTER_DPI = 300;
const DEFAULT_MAX_RASTERIZED_OBJECT_PIXELS = 20_000_000;

export function mmToPt(millimeters: number): number {
  return millimeters * POINTS_PER_MM;
}

function parseColor(value: string): RGB {
  const normalized = value.trim();
  const short = /^#([0-9a-f]{3})$/i.exec(normalized);
  const long = /^#([0-9a-f]{6})$/i.exec(normalized);
  const hex = short
    ? short[1]!.split('').map((part) => part + part).join('')
    : long?.[1];
  if (!hex) {
    throw new PdfEngineError('PDF_SCENE_INVALID', 'Color no soportado; usa #RGB o #RRGGBB.', {
      color: value,
    });
  }
  return rgb(
    Number.parseInt(hex.slice(0, 2), 16) / 255,
    Number.parseInt(hex.slice(2, 4), 16) / 255,
    Number.parseInt(hex.slice(4, 6), 16) / 255,
  );
}

function standardFontValue(name: PdfStandardFontName): string {
  const map: Record<PdfStandardFontName, string> = {
    Courier: StandardFonts.Courier,
    'Courier-Bold': StandardFonts.CourierBold,
    'Courier-Oblique': StandardFonts.CourierOblique,
    'Courier-BoldOblique': StandardFonts.CourierBoldOblique,
    Helvetica: StandardFonts.Helvetica,
    'Helvetica-Bold': StandardFonts.HelveticaBold,
    'Helvetica-Oblique': StandardFonts.HelveticaOblique,
    'Helvetica-BoldOblique': StandardFonts.HelveticaBoldOblique,
    'Times-Roman': StandardFonts.TimesRoman,
    'Times-Bold': StandardFonts.TimesRomanBold,
    'Times-Italic': StandardFonts.TimesRomanItalic,
    'Times-BoldItalic': StandardFonts.TimesRomanBoldItalic,
  };
  return map[name];
}

interface DrawRectPt {
  readonly cx: number;
  readonly cy: number;
  readonly width: number;
  readonly height: number;
  readonly rotationClockwiseDeg: number;
}

function transformRect(transform: SceneTransformMm): DrawRectPt {
  return {
    cx: mmToPt(transform.cxMm),
    cy: mmToPt(transform.cyMm),
    width: mmToPt(transform.widthMm * transform.scale),
    height: mmToPt(transform.heightMm * transform.scale),
    rotationClockwiseDeg: transform.rotationDeg,
  };
}

function originForCenteredRotation(
  rect: DrawRectPt,
  pageHeightPt: number,
): { x: number; y: number; rotationPdfDeg: number } {
  const centerX = rect.cx;
  const centerY = pageHeightPt - rect.cy;
  const rotationPdfDeg = -rect.rotationClockwiseDeg;
  const radians = (rotationPdfDeg * Math.PI) / 180;
  const offsetX = Math.cos(radians) * (rect.width / 2) - Math.sin(radians) * (rect.height / 2);
  const offsetY = Math.sin(radians) * (rect.width / 2) + Math.cos(radians) * (rect.height / 2);
  return { x: centerX - offsetX, y: centerY - offsetY, rotationPdfDeg };
}

function fittedTransform(
  naturalWidth: number,
  naturalHeight: number,
  scene: PdfSceneV1,
  fit: 'cover' | 'contain' | 'stretch',
): SceneTransformMm {
  if (fit === 'stretch') {
    return {
      cxMm: scene.widthMm / 2,
      cyMm: scene.heightMm / 2,
      widthMm: scene.widthMm,
      heightMm: scene.heightMm,
      scale: 1,
      rotationDeg: 0,
    };
  }
  const sourceRatio = naturalWidth / naturalHeight;
  const targetRatio = scene.widthMm / scene.heightMm;
  const cover = fit === 'cover';
  const useWidth = cover ? sourceRatio < targetRatio : sourceRatio > targetRatio;
  const widthMm = useWidth ? scene.widthMm : scene.heightMm * sourceRatio;
  const heightMm = useWidth ? scene.widthMm / sourceRatio : scene.heightMm;
  return {
    cxMm: scene.widthMm / 2,
    cyMm: scene.heightMm / 2,
    widthMm,
    heightMm,
    scale: 1,
    rotationDeg: 0,
  };
}

async function resolveRequiredAsset(
  assetId: string,
  options: PdfExportOptions,
): Promise<ResolvedPdfAsset> {
  const asset = await options.assetResolver.resolve(assetId);
  if (!asset) {
    throw new PdfEngineError('PDF_ASSET_MISSING', 'Falta un activo necesario para exportar.', {
      assetId,
    });
  }
  if (asset.assetId !== assetId || asset.bytes.length === 0) {
    throw new PdfEngineError('PDF_ASSET_INVALID', 'El resolver devolvio un activo invalido.', {
      assetId,
    });
  }
  return asset;
}

interface EmbeddedVisual {
  readonly kind: 'image' | 'pdf-page';
  readonly resource: PDFImage | PDFEmbeddedPage;
  readonly naturalWidth: number;
  readonly naturalHeight: number;
}

async function embedVisual(
  pdf: PDFDocument,
  asset: ResolvedPdfAsset,
  requestedPageIndex: number | undefined,
  targetWidthMm: number,
  targetHeightMm: number,
  options: PdfExportOptions,
  diagnostics: PdfExportDiagnostic[],
): Promise<EmbeddedVisual> {
  if (asset.mimeType === 'application/pdf') {
    const pageIndex = requestedPageIndex ?? asset.pdf?.selectedPageIndex ?? 0;
    const pageCount = asset.pdf?.pageCount;
    if (!Number.isInteger(pageIndex) || pageIndex < 0 || (pageCount !== undefined && pageIndex >= pageCount)) {
      throw new PdfEngineError('PDF_PAGE_OUT_OF_RANGE', 'La pagina PDF solicitada no existe.', {
        assetId: asset.assetId,
        pageIndex,
        pageCount,
      });
    }
    try {
      const [page] = await pdf.embedPdf(Uint8Array.from(asset.bytes), [pageIndex]);
      if (!page) throw new Error('pdf-lib no devolvio la pagina embebida');
      diagnostics.push({
        code: 'PDF_PAGE_EMBEDDED_VECTOR',
        message: 'La pagina PDF se embebio como pagina PDF, sin rasterizarla.',
        assetId: asset.assetId,
      });
      return { kind: 'pdf-page', resource: page, naturalWidth: page.width, naturalHeight: page.height };
    } catch (cause) {
      throw new PdfEngineError(
        'PDF_ASSET_INVALID',
        'No se pudo embeber la pagina del PDF importado.',
        { assetId: asset.assetId, pageIndex },
        { cause },
      );
    }
  }

  if (asset.mimeType === 'image/png') {
    const image = await pdf.embedPng(Uint8Array.from(asset.bytes));
    return { kind: 'image', resource: image, naturalWidth: image.width, naturalHeight: image.height };
  }
  if (asset.mimeType === 'image/jpeg') {
    const image = await pdf.embedJpg(Uint8Array.from(asset.bytes));
    return { kind: 'image', resource: image, naturalWidth: image.width, naturalHeight: image.height };
  }

  if (!options.rasterizer) {
    throw new PdfEngineError(
      'PDF_RASTERIZER_REQUIRED',
      'WEBP y SVG requieren un rasterizador browser por objeto antes de embeberse.',
      { assetId: asset.assetId, mimeType: asset.mimeType },
    );
  }
  const dpi = options.rasterDpi ?? DEFAULT_RASTER_DPI;
  const widthPx = Math.max(1, Math.ceil((targetWidthMm / 25.4) * dpi));
  const heightPx = Math.max(1, Math.ceil((targetHeightMm / 25.4) * dpi));
  const pixelCount = widthPx * heightPx;
  const limit = options.maxRasterizedObjectPixels ?? DEFAULT_MAX_RASTERIZED_OBJECT_PIXELS;
  if (pixelCount > limit) {
    throw new PdfEngineError(
      'PDF_RASTER_LIMIT',
      'El objeto SVG/WEBP supera el presupuesto de rasterizacion por objeto.',
      { assetId: asset.assetId, widthPx, heightPx, pixelCount, limit },
    );
  }
  const rasterized = await options.rasterizer.rasterize({
    asset,
    widthPx,
    heightPx,
    background: 'transparent',
  });
  const image = await pdf.embedPng(Uint8Array.from(rasterized.bytes));
  diagnostics.push({
    code: 'ASSET_RASTERIZED_PER_OBJECT',
    message: 'El SVG/WEBP se rasterizo solo al tamano requerido por este objeto.',
    assetId: asset.assetId,
  });
  return { kind: 'image', resource: image, naturalWidth: image.width, naturalHeight: image.height };
}

function drawEmbeddedVisual(
  page: PDFPage,
  embedded: EmbeddedVisual,
  transform: SceneTransformMm,
  opacity = 1,
): void {
  const rect = transformRect(transform);
  const origin = originForCenteredRotation(rect, page.getHeight());
  const common = {
    x: origin.x,
    y: origin.y,
    width: rect.width,
    height: rect.height,
    rotate: degrees(origin.rotationPdfDeg),
    opacity,
  };
  if (embedded.kind === 'image') page.drawImage(embedded.resource as PDFImage, common);
  else page.drawPage(embedded.resource as PDFEmbeddedPage, common);
}

async function drawAssetNode(
  pdf: PDFDocument,
  page: PDFPage,
  node: SceneAssetNode,
  options: PdfExportOptions,
  diagnostics: PdfExportDiagnostic[],
): Promise<void> {
  const asset = await resolveRequiredAsset(node.assetId, options);
  const targetWidthMm = node.transform.widthMm * node.transform.scale;
  const targetHeightMm = node.transform.heightMm * node.transform.scale;
  const embedded = await embedVisual(
    pdf,
    asset,
    node.pdfPageIndex,
    targetWidthMm,
    targetHeightMm,
    options,
    diagnostics,
  );
  drawEmbeddedVisual(page, embedded, node.transform, node.opacity ?? 1);
}

async function resolveFont(
  pdf: PDFDocument,
  node: SceneTextNode,
  options: PdfExportOptions,
  cache: Map<string, PDFFont>,
  diagnostics: PdfExportDiagnostic[],
): Promise<PDFFont> {
  const key = node.font.kind === 'standard' ? 'standard:' + node.font.name : 'asset:' + node.font.fontAssetId;
  const cached = cache.get(key);
  if (cached) return cached;
  try {
    if (node.font.kind === 'standard') {
      const font = await pdf.embedFont(standardFontValue(node.font.name));
      cache.set(key, font);
      return font;
    }
    if (!options.fontResolver) {
      throw new PdfEngineError(
        'PDF_FONT_BYTES_REQUIRED',
        'La fuente ' + node.font.family + ' requiere bytes WOFF/TTF/OTF provistos por FontResolver.',
        { fontAssetId: node.font.fontAssetId, family: node.font.family },
      );
    }
    const resolved = await options.fontResolver.resolve(node.font.fontAssetId);
    if (!resolved) {
      throw new PdfEngineError(
        'PDF_FONT_BYTES_REQUIRED',
        'No se encontraron los bytes embebibles de la fuente.',
        { fontAssetId: node.font.fontAssetId, family: node.font.family },
      );
    }
    if (
      (resolved.family !== undefined && resolved.family !== node.font.family) ||
      (node.font.weight !== undefined && resolved.weight !== node.font.weight) ||
      (node.font.style !== undefined && resolved.style !== node.font.style)
    ) {
      throw new PdfEngineError(
        'PDF_FONT_INVALID',
        'El perfil de la fuente no coincide con familia, peso o estilo de la escena.',
        {
          requested: {
            family: node.font.family,
            weight: node.font.weight,
            style: node.font.style,
          },
          resolved: {
            family: resolved.family,
            weight: resolved.weight,
            style: resolved.style,
          },
        },
      );
    }
    pdf.registerFontkit(fontkit);
    const font = await pdf.embedFont(Uint8Array.from(resolved.bytes), { subset: true });
    cache.set(key, font);
    diagnostics.push({
      code: 'FONT_EMBEDDED',
      message: 'La fuente se incorporo al PDF desde bytes ' + resolved.format.toUpperCase() + ' provistos.',
      nodeId: node.id,
      assetId: resolved.fontAssetId,
    });
    return font;
  } catch (cause) {
    if (cause instanceof PdfEngineError) throw cause;
    throw new PdfEngineError(
      'PDF_FONT_INVALID',
      'La fuente no pudo medirse o embeberse en el PDF.',
      { nodeId: node.id },
      { cause },
    );
  }
}

function textOrigin(
  node: SceneTextNode,
  font: PDFFont,
  fontSizePt: number,
  pageHeightPt: number,
): { x: number; y: number; rotationPdfDeg: number } {
  const textWidth = font.widthOfTextAtSize(node.text, fontSizePt);
  const textHeight = font.heightAtSize(fontSizePt, { descender: true });
  const rect: DrawRectPt = {
    cx: mmToPt(node.transform.cxMm),
    cy: mmToPt(node.transform.cyMm),
    width: textWidth,
    height: textHeight,
    rotationClockwiseDeg: node.transform.rotationDeg,
  };
  return originForCenteredRotation(rect, pageHeightPt);
}

async function drawTextNode(
  pdf: PDFDocument,
  page: PDFPage,
  node: SceneTextNode,
  options: PdfExportOptions,
  cache: Map<string, PDFFont>,
  diagnostics: PdfExportDiagnostic[],
): Promise<void> {
  const font = await resolveFont(pdf, node, options, cache, diagnostics);
  const size = mmToPt(node.fontSizeMm * node.transform.scale);
  let origin: { x: number; y: number; rotationPdfDeg: number };
  try {
    origin = textOrigin(node, font, size, page.getHeight());
  } catch (cause) {
    throw new PdfEngineError(
      'PDF_FONT_INVALID',
      'La fuente no puede codificar o medir el texto solicitado.',
      { nodeId: node.id, text: node.text },
      { cause },
    );
  }
  const common = {
    size,
    font,
    rotate: degrees(origin.rotationPdfDeg),
    opacity: node.opacity ?? 1,
  };
  if (node.stroke && node.stroke.widthMm > 0) {
    const opacity = node.opacity ?? 1;
    const graphicsStateKey = opacity === 1
      ? undefined
      : page.node.newExtGState('GS', pdf.context.obj({
        Type: 'ExtGState',
        ca: opacity,
        CA: opacity,
      }));
    const fontKey = page.node.newFontDictionary(font.name, font.ref);

    page.pushOperators(
      pushGraphicsState(),
      ...(graphicsStateKey ? [setGraphicsState(graphicsStateKey)] : []),
      beginText(),
      setFillingColor(parseColor(node.fillColor)),
      setStrokingColor(parseColor(node.stroke.color)),
      setLineWidth(mmToPt(node.stroke.widthMm * node.transform.scale)),
      setTextRenderingMode(TextRenderingMode.FillAndOutline),
      setFontAndSize(fontKey, size),
      rotateAndSkewTextDegreesAndTranslate(origin.rotationPdfDeg, 0, 0, origin.x, origin.y),
      showText(font.encodeText(node.text)),
      endText(),
      popGraphicsState(),
    );
    diagnostics.push({
      code: 'TEXT_STROKE_NATIVE',
      message: 'El texto y su contorno se dibujaron una sola vez con relleno y trazo vectoriales.',
      nodeId: node.id,
    });
    return;
  }
  page.drawText(node.text, {
    ...common,
    x: origin.x,
    y: origin.y,
    color: parseColor(node.fillColor),
  });
}

function pushCircleClip(page: PDFPage): void {
  const cx = page.getWidth() / 2;
  const cy = page.getHeight() / 2;
  const rx = page.getWidth() / 2;
  const ry = page.getHeight() / 2;
  const k = 0.5522847498307936;
  page.pushOperators(
    pushGraphicsState(),
    moveTo(cx + rx, cy),
    appendBezierCurve(cx + rx, cy + k * ry, cx + k * rx, cy + ry, cx, cy + ry),
    appendBezierCurve(cx - k * rx, cy + ry, cx - rx, cy + k * ry, cx - rx, cy),
    appendBezierCurve(cx - rx, cy - k * ry, cx - k * rx, cy - ry, cx, cy - ry),
    appendBezierCurve(cx + k * rx, cy - ry, cx + rx, cy - k * ry, cx + rx, cy),
    closePath(),
    clip(),
    endPath(),
  );
}

async function drawBackground(
  pdf: PDFDocument,
  page: PDFPage,
  scene: PdfSceneV1,
  options: PdfExportOptions,
  diagnostics: PdfExportDiagnostic[],
): Promise<void> {
  if (scene.background.kind === 'white') return;
  const asset = await resolveRequiredAsset(scene.background.assetId, options);
  const target = scene.background.transform ?? {
    cxMm: scene.widthMm / 2,
    cyMm: scene.heightMm / 2,
    widthMm: scene.widthMm,
    heightMm: scene.heightMm,
    scale: 1,
    rotationDeg: 0,
  };
  const embedded = await embedVisual(
    pdf,
    asset,
    scene.background.pdfPageIndex,
    target.widthMm * target.scale,
    target.heightMm * target.scale,
    options,
    diagnostics,
  );
  const transform = scene.background.transform ?? fittedTransform(
    embedded.naturalWidth,
    embedded.naturalHeight,
    scene,
    scene.background.fit,
  );
  drawEmbeddedVisual(page, embedded, transform);
}

function drawStickerCutLine(
  page: PDFPage,
  scene: PdfSceneV1,
  diagnostics: PdfExportDiagnostic[],
): void {
  if (scene.product.kind !== 'sticker') return;
  const metrics = calculateV46StickerCutLineMetrics(scene.widthMm, scene.heightMm);
  const width = mmToPt(metrics.lineWidthMm);
  const inset = mmToPt(metrics.insetMm);
  if (scene.product.shape === 'circle') {
    page.drawEllipse({
      x: page.getWidth() / 2,
      y: page.getHeight() / 2,
      xScale: Math.max(0.1, page.getWidth() / 2 - inset),
      yScale: Math.max(0.1, page.getHeight() / 2 - inset),
      borderColor: rgb(0.07, 0.07, 0.07),
      borderWidth: width,
    });
  } else {
    page.drawRectangle({
      x: inset,
      y: inset,
      width: Math.max(0.1, page.getWidth() - inset * 2),
      height: Math.max(0.1, page.getHeight() - inset * 2),
      borderColor: rgb(0.07, 0.07, 0.07),
      borderWidth: width,
    });
  }
  diagnostics.push({
    code: 'STICKER_CUT_LINE_V46_300_DPI',
    message:
      'Linea de corte V4.6: ' + String(metrics.lineWidthPx) + ' px (' +
      metrics.lineWidthMm.toFixed(6) + ' mm), inset ' + metrics.insetPx.toFixed(2) +
      ' px a 300 DPI.',
  });
}

export async function exportPdfScene(
  scene: PdfSceneV1,
  options: PdfExportOptions,
): Promise<PdfArtifact> {
  assertPdfScene(scene);
  const widthPt = mmToPt(scene.widthMm);
  const heightPt = mmToPt(scene.heightMm);
  const diagnostics: PdfExportDiagnostic[] = [];
  try {
    const pdf = await PDFDocument.create();
    const now = options.now ?? new Date();
    pdf.setTitle(options.title ?? 'DG embalajes - archivo para impresion');
    pdf.setAuthor('DG embalajes');
    pdf.setCreator('@dg-embalajes/pdf-engine');
    pdf.setProducer('pdf-lib');
    pdf.setCreationDate(now);
    pdf.setModificationDate(now);
    const page = pdf.addPage([widthPt, heightPt]);
    page.drawRectangle({
      x: 0,
      y: 0,
      width: page.getWidth(),
      height: page.getHeight(),
      color: rgb(1, 1, 1),
    });
    diagnostics.push({ code: 'OPAQUE_WHITE_BASE', message: 'La pagina tiene una base blanca opaca.' });
    const clipCircle = scene.product.kind === 'sticker' && scene.product.shape === 'circle';
    if (clipCircle) pushCircleClip(page);
    await drawBackground(pdf, page, scene, options, diagnostics);
    const fontCache = new Map<string, PDFFont>();
    for (const node of scene.nodes) {
      if (node.type === 'asset') await drawAssetNode(pdf, page, node, options, diagnostics);
      else await drawTextNode(pdf, page, node, options, fontCache, diagnostics);
    }
    if (clipCircle) page.pushOperators(popGraphicsState());
    drawStickerCutLine(page, scene, diagnostics);
    const bytes = await pdf.save({ useObjectStreams: false, addDefaultPage: false });
    return {
      bytes,
      fileName: buildPdfFileName(scene),
      widthMm: scene.widthMm,
      heightMm: scene.heightMm,
      widthPt,
      heightPt,
      pageCount: 1,
      diagnostics,
    };
  } catch (cause) {
    if (cause instanceof PdfEngineError) throw cause;
    throw new PdfEngineError(
      'PDF_EXPORT_FAILED',
      'No se pudo construir el PDF fisico.',
      { documentId: scene.documentId },
      { cause },
    );
  }
}
